import React, { useMemo } from "react";

import { css } from "@emotion/css";
import { useSearch } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { MedicalDevice } from "@/models/MedicalDeviceModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Tab } from "@/uiKit/atoms/Tab.tsx";
import { TabList } from "@/uiKit/atoms/TabList.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { DateUtils, toDateTime } from "@/utils/dateUtils.ts";
import { formatNumber } from "@/utils/mathUtils.ts";

import { InsulinPumpProfileContent } from "./InsulinPumpProfileContent";

export type InsulinPumpSettingsProps = {
  devices: MedicalDevice[];
};

export const InsulinPumpSettings: React.FC<InsulinPumpSettingsProps> = ({ devices }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const pumps = devices
    .filter(
      device =>
        device.kind === "pump" &&
        device.lastUpload &&
        toDateTime(device.lastUpload) > DateTime.now().minus({ months: 6 }),
    )
    .sort((a, b) => DateUtils.compareDateTimes(b.lastUpload, a.lastUpload));
  const pump = pumps.length > 0 ? pumps[0] : undefined;

  const profiles = useMemo(
    () => pump?.insulinPump?.profiles.sort((a, b) => (a.currentProfile ? -1 : a.name.localeCompare(b.name))) ?? [],
    [pump],
  );

  const selectedProfileId =
    useSearch({
      from: "/_practitioner/patients/$patientId/",
    }).pump_profile || profiles[0]?.id;

  const selectedProfile = profiles.find(profile => profile.id === selectedProfileId);
  if (!pump?.insulinPump || profiles.length === 0 || !selectedProfile) {
    return null;
  }

  return (
    <Card className={styles.container}>
      <CardHeader title={t("insulinPump.profiles")} />
      <CardContent className={styles.content}>
        <TabList id="insulin-pump-profiles-tabs" selectedTab={selectedProfileId}>
          {profiles.map(profile => (
            <Tab
              key={profile.id}
              id={`insulin-pump-profiles-tab-${profile.name}`}
              label={`${t("insulinPump.profile")} : ${profile.name}`}
              value={profile.id}
              selected={profile.id === selectedProfileId}
              link={{
                to: ".",
                search: {
                  pump_profile: profile.id,
                },
              }}
              color="purple"
            />
          ))}
        </TabList>
        <div className={styles.tabContent}>
          <InsulinPumpProfileContent profile={selectedProfile} className={styles.profileTable} />

          <div className={styles.pumpInfo}>
            <div className={styles.infoItem}>
              <Typography variant="body">{t("insulinPump.model")}</Typography>
              <Typography variant="subtitle">
                {pump.displayName ? pump.displayName : pump.name.capitalizeAll()}
              </Typography>
            </div>

            <div className={styles.infoItem}>
              <Typography variant="body">{t("insulinPump.lastUpload")}</Typography>
              <Typography variant="subtitle">{toDateTime(pump.lastUpload).toLocaleString()}</Typography>
            </div>
            {selectedProfile.activeInsulinTime && (
              <div className={styles.infoItem}>
                <Typography variant="body">{t("insulinPump.activeInsulinTime")}</Typography>
                <Typography variant="subtitle">
                  {`${formatNumber(selectedProfile.activeInsulinTime / 60000, 0)} ${t("insulinPump.units.activeInsulinTime")}`}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css``,
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
  `,
  tabContent: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(16)};
    flex-wrap: wrap;
  `,
  profileTable: css`
    flex-grow: 1;
  `,
  pumpInfo: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(16)};
    justify-content: center;
  `,
  infoItem: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  title: css`
    margin-bottom: ${theme.spacing(4)};
  `,
});
